import axios from "axios";
import type { ApiProblem } from "@/lib/types";

/**
 * Títulos genéricos que el backend usaba (o usa) en ProblemDetails y que NO dicen nada útil
 * al usuario: si no hay otra cosa, se prefiere un mensaje por código de estado.
 */
const TITULOS_GENERICOS = new Set(["Validation failed", "VALIDACION", "SIN_PERMISO", "Forbidden", "Unauthorized"]);

const MENSAJE_SIN_PERMISO = "No tienes permiso para realizar esta acción.";

/** Mensajes por código de estado cuando el servidor no manda detalle. */
function mensajePorEstado(status: number | undefined): string | undefined {
  switch (status) {
    case 400:
      return "Los datos enviados no son válidos.";
    case 401:
      return "Tu sesión expiró · vuelve a iniciar sesión.";
    case 403:
      return MENSAJE_SIN_PERMISO;
    case 404:
      return "El recurso no existe o no tienes acceso a él.";
    case 409:
      return "La operación entra en conflicto con el estado actual · recarga e intenta de nuevo.";
    case 429:
      return "Demasiados intentos · espera un minuto antes de reintentar.";
    default:
      return status && status >= 500 ? "Ocurrió un error en el servidor · intenta más tarde." : undefined;
  }
}

/** Mensajes de validación del diccionario `errors` (ValidationProblemDetails), sin duplicados. */
export function mensajesDeValidacion(problem: ApiProblem | undefined): string[] {
  if (!problem?.errors || typeof problem.errors !== "object") return [];
  const vistos = new Set<string>();
  for (const lista of Object.values(problem.errors)) {
    for (const m of Array.isArray(lista) ? lista : [lista]) {
      const texto = typeof m === "string" ? m.trim() : "";
      if (texto) vistos.add(texto);
    }
  }
  return [...vistos];
}

function conPunto(texto: string): string {
  return /[.!?…]$/.test(texto) ? texto : `${texto}.`;
}

/**
 * Hotspot 80 · extrae un mensaje humano legible de cualquier error (Axios, Error, otro).
 * Usado por todos los `onError` de mutations para feedback consistente. Orden:
 * 1. Lista de errores de validación del servidor (`errors`) — antes se ignoraba y el toast
 *    decía solo «Validation failed».
 * 2. `detail` del ProblemDetails (incluye el «No tienes permiso para …» de los 403 por permiso).
 * 3. `title` si no es genérico.
 * 4. Mensaje por código de estado (403 → «No tienes permiso…», nunca «Request failed with status code 403»).
 */
export function extractApiMessage(err: unknown, fallback = "Error"): string {
  if (axios.isAxiosError<ApiProblem>(err)) {
    const status = err.response?.status;
    const data = err.response?.data;
    const problem = data && typeof data === "object" && !(data instanceof Blob) ? data : undefined;

    const validacion = mensajesDeValidacion(problem);
    if (validacion.length > 0) return validacion.map(conPunto).join(" ");

    if (problem?.detail?.trim()) return problem.detail.trim();
    if (problem?.title?.trim() && !TITULOS_GENERICOS.has(problem.title.trim())) return problem.title.trim();

    return mensajePorEstado(status) ?? err.message ?? fallback;
  }
  return err instanceof Error ? err.message : fallback;
}

/** Título corto del toast según el tipo de error. */
export function extractApiTitle(err: unknown, fallback = "Error"): string {
  if (!axios.isAxiosError(err)) return fallback;
  switch (err.response?.status) {
    case 400:
      return "Revisa los datos";
    case 403:
      return "Sin permiso";
    default:
      return fallback;
  }
}
